
import React, { createContext, useContext, ReactNode } from 'react';
import { useRewardSystem } from '@/hooks/useRewardSystem';
import { VintageSlotMachine } from './VintageSlotMachine';
import { RewardTriggerSource } from './types';

interface RewardSystemContextType {
  triggerReward: (triggerSource: RewardTriggerSource) => Promise<void>;
  checkRewardEligibility: (triggerSource: RewardTriggerSource) => Promise<boolean>;
}

const RewardSystemContext = createContext<RewardSystemContextType | null>(null);

export const useRewardSystemContext = () => {
  const context = useContext(RewardSystemContext);
  if (!context) {
    throw new Error('useRewardSystemContext must be used within a RewardSystemProvider');
  }
  return context;
};

interface RewardSystemProviderProps {
  children: ReactNode;
}

export const RewardSystemProvider = ({ children }: RewardSystemProviderProps) => {
  const {
    isSlotMachineOpen,
    currentTriggerSource,
    setIsSlotMachineOpen,
    triggerReward,
    onRewardClaimed,
    checkRewardEligibility
  } = useRewardSystem();

  // Enhanced reward claimed handler with better event dispatching
  const handleRewardClaimed = (reward: any) => {
    console.log('Reward claimed in provider:', reward);
    
    // Call the original handler
    onRewardClaimed(reward);
    
    // Dispatch custom event with more details
    const customEvent = new CustomEvent('rewardClaimed', {
      detail: {
        ...reward,
        timestamp: Date.now(),
        source: currentTriggerSource
      }
    });
    
    console.log('Dispatching rewardClaimed event:', customEvent.detail);
    window.dispatchEvent(customEvent);
  };

  return (
    <RewardSystemContext.Provider value={{ triggerReward, checkRewardEligibility }}>
      {children}
      
      {isSlotMachineOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="max-w-md w-full mx-4">
            <VintageSlotMachine />
            <div className="mt-4 text-center">
              <button
                onClick={() => setIsSlotMachineOpen(false)}
                className="text-white hover:text-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </RewardSystemContext.Provider>
  );
};
